import { GitHub, Google } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  Divider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useAuthUrlQuery } from 'auth/hook/useAuthUrlQuery'
import { authSocial, authViaForm } from 'auth/utils'
import { FormWrapper } from 'auth/utils/FormWrapper'
import Button from 'common/components/Button'
import { ErrorMessage } from 'common/components/ErrorMessage'
import { Link } from 'common/components/Link'
import { useEffect, useState } from 'react'
import { useMount } from 'react-use'
import { ResetPasswordView } from './ResetPasswordView'
import { Checkbox } from 'common/components/Checkbox'

export const RegisterAndSignInView = () => {
  const [loading, setLoading] = useState(false)
  const [loadingSocial, setLoadingSocial] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreementAccepted, setAgreementAccepted] = useState(false)

  const { query, setQuery } = useAuthUrlQuery()

  useEffect(() => {
    setError('')
  }, [email, password, confirmPassword])
  /**
   * Authenticate via a social provider
   * @param {*} method
   */
  const authViaSocial = async (method) => {
    setLoadingSocial(method)

    try {
      await authSocial(method)
    } catch (error) {
      setError(error.message || 'Sorry, an unknown error occurred.  Please try again later.')
    }
    setLoadingSocial(false)
  }

  /**
   * Authenticate via the form.  Handles Register & Sign-in
   */
  const handleAuthFormSubmit = async (event) => {
    event.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (query.view === 'register' && !agreementAccepted) {
        throw new Error('You must accept the Customer Agreement')
      }

      await authViaForm(query.view, email, password, confirmPassword)
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }
  /**
   * If the social auth query param is set, then automatically use that mechanism
   */
  useMount(() => {
    if (query.socialAuthType) {
      authViaSocial(query.socialAuthType)
    }
  }, [query.socialAuthType])

  if (query.view === 'password') {
    return <ResetPasswordView email={email} setEmail={setEmail} setQuery={setQuery} />
  }
  const disabled = query.view === 'register' && !agreementAccepted
  return (
    <>
      {
        /**
         * Show an alert box with action required for users coming from AWS Marketplace
         */
        query?.subscriptionId && (
          <Alert color="secondary">
            <AlertTitle>Action Required: Link Your AWS Subscription</AlertTitle>
            Thank you for subscribing to the Serverless Framework on the AWS Marketplace. Please
            register or log in to link your organization with your AWS subscription. You'll then be
            able to invite team members, monitor your serverless apps, and create license keys to be
            used with the Serverless Framework.
          </Alert>
        )
      }
      <Tabs
        centered
        value={query.view}
        onChange={(_, newValue) => setQuery({ view: newValue })}
        aria-label="login tabs"
      >
        <Tab
          label="Register"
          value="register"
          sx={{
            margin: 0,
            minWidth: '50%',
          }}
        />
        <Tab
          label="Login"
          value="signIn"
          sx={{
            margin: 0,
            minWidth: '50%',
          }}
        />
      </Tabs>

      {/**
       * Form input
       */}
      <FormWrapper onSubmit={handleAuthFormSubmit}>
        <TextField
          InputLabelProps={{
            color: 'secondary',
          }}
          label="Email"
          name="email"
          autoFocus
          fullWidth
          value={email}
          color={'primary'}
          onChange={(e) => setEmail(e.target.value)}
        />

        <TextField
          InputLabelProps={{
            color: 'secondary',
          }}
          label="Password"
          name="password"
          type="password"
          fullWidth
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {query.view === 'register' && (
          <Collapse in={query.view === 'register'}>
            <TextField
              InputLabelProps={{
                color: 'secondary',
              }}
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Collapse>
        )}
        {query.view === 'register' && (
          <Collapse in={query.view === 'register'}>
            <Checkbox
              checked={agreementAccepted}
              onChange={(event) => setAgreementAccepted(event.target.checked)}
              label={
                <Typography variant="textSecondary" color="text.secondary">
                  I accept the{' '}
                  <Link
                    to="https://www.serverless.com/customer-agreement"
                    underline="always"
                    showIcon
                  >
                    Serverless Customer Agreement
                  </Link>
                </Typography>
              }
            />
          </Collapse>
        )}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ width: '100%', mt: 1 }}
          loading={loading}
          disabled={disabled}
        >
          {query.view === 'register' ? 'Register' : 'Login'}
        </Button>
        <Divider color="text.secondary" sx={{ color: 'text.secondary' }}>
          or
        </Divider>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
          }}
        >
          <GithubOAuthButton
            disabledReason={disabled && 'Please accept the Customer Agreement to continue'}
            onClick={() => authViaSocial('github')}
            loading={loadingSocial === 'github'}
            disabled={disabled}
          >
            Continue with GitHub
          </GithubOAuthButton>
          <GoogleOAuthButton
            disabledReason={disabled && 'Please accept the Customer Agreement to continue'}
            onClick={() => authViaSocial('google-oauth2')}
            loading={loadingSocial === 'google-oauth2'}
            disabled={disabled}
          >
            Continue with Google
          </GoogleOAuthButton>
        </Box>
        {error && <ErrorMessage message={error} />}

        {query.view === 'signIn' && (
          <Link
            sx={{
              marginLeft: 'auto',
            }}
            underline="always"
            onClick={(e) => {
              e.preventDefault()
              setQuery({ view: 'password' })
            }}
          >
            Forgot password?
          </Link>
        )}
      </FormWrapper>
    </>
  )
}

const GoogleOAuthButton = (props) => (
  <Button sx={{ width: '100%' }} {...props}>
    <Google sx={{ margin: '0 12px 0 0' }} /> Google
  </Button>
)

const GithubOAuthButton = (props) => (
  <Button sx={{ width: '100%' }} {...props}>
    <GitHub sx={{ margin: '0 12px 0 0' }} /> Github
  </Button>
)
